import React, { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import { FormControl } from "react-bootstrap";
import { DeleteFileSvg, RenameFileSvg } from "../Svg/allsvg";
import {
  createDepartment,
  renameDepartment,
  deleteDepartment,
} from "../../libs/API";

const NEW_DEPARTMENT = "new";

function AlertIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
    </svg>
  );
}

const repositoryCountLabel = (count) =>
  `${count} ${count === 1 ? "repository" : "repositories"}`;

export default function ManageDepartmentsPopup({
  show,
  onHide,
  clientId,
  clientName,
  departments,
  repositories,
  onChanged,
}) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Where the error is shown: under the "new" input or under a department row.
  const [errorTarget, setErrorTarget] = useState(null);
  // Existing department highlighted when a duplicate name is entered.
  const [highlightId, setHighlightId] = useState(null);
  const newInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setNewName("");
      setEditingId(null);
      setSearch("");
      setError("");
      setErrorTarget(null);
      setHighlightId(null);
    }
  }, [show]);

  // "Dept Not Assigned" is the system department: never listed or editable.
  const editableDepartments = departments.filter((d) => !d.isSystem);
  const searchTerm = search.trim().toLowerCase();
  const visibleDepartments = searchTerm
    ? editableDepartments.filter((d) =>
        d.departmentName.toLowerCase().includes(searchTerm),
      )
    : editableDepartments;

  // Counted from the repositories already loaded in the grid (no extra API call).
  const repositoryCount = (departmentId) =>
    (repositories || []).filter((r) => r.departmentId === departmentId).length;

  // The API fails with an unhandled duplicate-key error (no readable response), so check here first.
  const findDuplicate = (name, exceptId) =>
    departments.find(
      (d) =>
        d.departmentId !== exceptId &&
        d.departmentName.trim().toLowerCase() === name.toLowerCase(),
    );

  const duplicateMessage = (name) =>
    clientName
      ? `“${name}” already exists in ${clientName}.`
      : `“${name}” already exists.`;

  const showError = (target, message, duplicateId = null) => {
    setErrorTarget(target);
    setError(message);
    setHighlightId(duplicateId);
  };

  const clearErrorFor = (target) => {
    if (errorTarget === target) showError(null, "");
  };

  const runAction = async (target, action) => {
    setLoading(true);
    showError(null, "");
    try {
      await action();
      await onChanged();
      return true;
    } catch (err) {
      showError(target, err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      showError(NEW_DEPARTMENT, "Department name is required.");
      return;
    }
    const duplicate = findDuplicate(name);
    if (duplicate) {
      showError(
        NEW_DEPARTMENT,
        duplicateMessage(duplicate.departmentName),
        duplicate.departmentId,
      );
      return;
    }
    const ok = await runAction(NEW_DEPARTMENT, () =>
      createDepartment(clientId, name),
    );
    if (ok) {
      setNewName("");
      setTimeout(() => newInputRef.current && newInputRef.current.focus(), 0);
    }
  };

  const startEdit = (department) => {
    setEditingId(department.departmentId);
    setEditingName(department.departmentName);
    showError(null, "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    showError(null, "");
  };

  const handleRename = async () => {
    const name = editingName.trim();
    if (!name) {
      showError(editingId, "Department name is required.");
      return;
    }
    const duplicate = findDuplicate(name, editingId);
    if (duplicate) {
      showError(
        editingId,
        duplicateMessage(duplicate.departmentName),
        duplicate.departmentId,
      );
      return;
    }
    const ok = await runAction(editingId, () =>
      renameDepartment(editingId, name),
    );
    if (ok) setEditingId(null);
  };

  const handleDelete = (department) =>
    runAction(department.departmentId, async () => {
      try {
        await deleteDepartment(department.departmentId);
      } catch (err) {
        // The API's 409 text ends with "Reassign first."; show only the first part.
        throw new Error(err.message.replace(/\s*Reassign first\.?$/, ""));
      }
    });

  const hasError = (target) => !!error && errorTarget === target;
  const errorId = (target) => `dept-error-${target}`;

  const renderError = (target) =>
    hasError(target) ? (
      <div id={errorId(target)} className="dept-modal-error" role="alert">
        <AlertIcon />
        <span>{error}</span>
      </div>
    ) : null;

  const departmentCountLabel = searchTerm
    ? `${visibleDepartments.length} of ${editableDepartments.length} departments`
    : `${editableDepartments.length} ${
        editableDepartments.length === 1 ? "department" : "departments"
      }`;

  return (
    <Popup
      open={show}
      closeOnDocumentClick={false}
      onClose={onHide}
      contentStyle={{ width: "600px", borderRadius: "8px", padding: 0 }}
    >
      <div className="dept-modal">
        <div className="dept-modal-header">
          <h4>
            Manage Departments
            {clientName ? ` · ${clientName}` : ""}
          </h4>
          <button
            type="button"
            className="dept-modal-close"
            onClick={onHide}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="dept-modal-add">
          <label htmlFor="newDepartmentName">New department</label>
          <div className="dept-modal-add-row">
            <FormControl
              id="newDepartmentName"
              type="text"
              placeholder="Enter a department name"
              inputRef={(ref) => (newInputRef.current = ref)}
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                clearErrorFor(NEW_DEPARTMENT);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              disabled={loading}
              className={hasError(NEW_DEPARTMENT) ? "field-invalid" : ""}
              aria-invalid={hasError(NEW_DEPARTMENT)}
              aria-describedby={
                hasError(NEW_DEPARTMENT) ? errorId(NEW_DEPARTMENT) : undefined
              }
            />
            <button
              type="button"
              className="btn brand-button"
              onClick={handleAdd}
              disabled={loading || !newName.trim()}
            >
              {loading ? "..." : "Add"}
            </button>
          </div>
          {renderError(NEW_DEPARTMENT)}
        </div>

        <div className="dept-modal-list-header">
          <span className="dept-modal-count">{departmentCountLabel}</span>
          <FormControl
            type="text"
            placeholder="Search..."
            aria-label="Search departments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dept-modal-search"
          />
        </div>

        <div className="dept-modal-list">
          {editableDepartments.length === 0 ? (
            <div className="dept-modal-empty">
              No departments yet. Add the first one above.
            </div>
          ) : visibleDepartments.length === 0 ? (
            <div className="dept-modal-empty">
              No departments match &ldquo;{search.trim()}&rdquo;.
            </div>
          ) : (
            visibleDepartments.map((department) => {
              const id = department.departmentId;
              const isEditing = editingId === id;
              return (
                <div
                  key={id}
                  className={
                    "dept-modal-row" +
                    (highlightId === id ? " dept-modal-row-highlight" : "")
                  }
                >
                  <div className="dept-modal-row-main">
                    {isEditing ? (
                      <FormControl
                        type="text"
                        aria-label="Department name"
                        value={editingName}
                        onChange={(e) => {
                          setEditingName(e.target.value);
                          clearErrorFor(id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleRename();
                          } else if (e.key === "Escape") {
                            cancelEdit();
                          }
                        }}
                        disabled={loading}
                        className={hasError(id) ? "field-invalid" : ""}
                        aria-invalid={hasError(id)}
                        aria-describedby={
                          hasError(id) ? errorId(id) : undefined
                        }
                      />
                    ) : (
                      <div className="dept-modal-row-text">
                        <div className="dept-modal-name">
                          {department.departmentName}
                        </div>
                        <div className="dept-modal-meta">
                          {repositoryCountLabel(repositoryCount(id))}
                        </div>
                      </div>
                    )}
                    <div className="dept-modal-row-actions">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm brand-button"
                            onClick={handleRename}
                            disabled={loading}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={cancelEdit}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm dept-action-button dept-edit-button"
                            onClick={() => startEdit(department)}
                            disabled={loading}
                          >
                            <RenameFileSvg />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm dept-action-button dept-delete-button"
                            onClick={() => handleDelete(department)}
                            disabled={loading}
                          >
                            <DeleteFileSvg />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {renderError(id)}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Popup>
  );
}
